// Copyright 2020 Signal Messenger, LLC
// SPDX-License-Identifier: AGPL-3.0-only

import React from "react";
import type { SetRendererCanvasType } from "../state/ducks/calling";
import { CallMode } from "../types/CallDisposition";
import type { ActiveCallType } from "../types/Calling";
import type { LocalizerType } from "../types/Util";
import { CallBackgroundBlur } from "./CallBackgroundBlur";
import { DirectCallRemoteParticipant } from "./DirectCallRemoteParticipant";
import { AvatarGroup } from "components";
import { useSelector } from "react-redux";
import type { CallingImageDataCache } from "./CallManager";

const assertDev = window.electronLibs.ringRtc.assertDev;
const missingCaseError = window.electronLibs.ringRtc.missingCaseError;
const isReconnecting = window.electronLibs.ringRtc.isReconnecting;

// This value should be kept in sync with the hard-coded CSS height. It should also be
//   less than `MAX_FRAME_HEIGHT`.

function NoVideo({ activeCall }: { activeCall: ActiveCallType; i18n: LocalizerType }): JSX.Element {
  const { id: conversationId } = activeCall.conversation;
  const callingGroupDetailRingMap = useSelector((state: any) => state.callingRedux.callingGroupDetailRing);
  const callingGroupDetailRing = callingGroupDetailRingMap?.[conversationId];

  return (
    <div className="module-calling-pip__video--remote">
      <CallBackgroundBlur avatarUrl={callingGroupDetailRing?.avatarId}>
        <div className="module-calling-pip__video--avatar">
          <div className="module-Avatar">
            <AvatarGroup
              avatarId={callingGroupDetailRing?.avatarId}
              prefixKey={callingGroupDetailRing?.prefixKey}
              group={{
                groupType: callingGroupDetailRing?.groupType,
                groupName: callingGroupDetailRing?.groupName,
                privateF: callingGroupDetailRing?.privateF,
              }}
              size={48}
            />
          </div>
        </div>
      </CallBackgroundBlur>
    </div>
  );
}

export type PropsType = {
  activeCall: ActiveCallType;
  i18n: LocalizerType;
  imageDataCache: React.RefObject<CallingImageDataCache>;
  setRendererCanvas: (_: SetRendererCanvasType) => void;
};

export function CallingPipRemoteVideo({ activeCall, i18n, setRendererCanvas }: PropsType): JSX.Element {
  const { conversation } = activeCall;

  switch (activeCall.callMode) {
    case CallMode.Direct: {
      const { hasRemoteVideo } = activeCall.remoteParticipants[0];
      if (!hasRemoteVideo) {
        return <NoVideo activeCall={activeCall} i18n={i18n} />;
      }
      assertDev(
        conversation.type === "direct",
        "CallingPipRemoteVideo for direct call must be associated with direct conversation",
      );
      return (
        <div className="module-calling-pip__video--remote">
          <DirectCallRemoteParticipant
            conversation={conversation}
            hasRemoteVideo={hasRemoteVideo}
            i18n={i18n}
            isReconnecting={isReconnecting(activeCall)}
            setRendererCanvas={setRendererCanvas}
          />
        </div>
      );
    }
    default:
      throw missingCaseError(activeCall);
  }
}
